import {
  useAuthoredRows,
  useCreateAuthoredRow,
  useDeleteAuthoredRow,
  useUpdateAuthoredRow,
} from 'components/IAP/hooks/icsAuthoredFormHooks';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import { BoxInfo } from '../icsBoxPrimitives';
import SaveableField from '../SaveableField';
import {
  ICS217A_BOX_LABELS,
  ICS217A_COLUMNS,
  ICS217A_DESCRIPTION_HINT,
  ICS217A_FREQUENCY_BAND_HINT,
} from '../ics217aBoxLabels';
import {
  BoxRowTable,
  NewCgFormCard,
  NewCgHeaderBoxes,
  NewCgPreparedByBox,
} from './newCgFormBoxes';

/**
 * ICS 217A-CG, boxed as the sheet prints it.
 *
 * Boxes 4 and 5 are one blank each for the whole worksheet, so they are kept
 * on the first channel row and applied to every row on save — the export reads
 * them off the first row too.
 */
const Ics217ACard = () => {
  const { effectivePeriodNumber } = useICSContext();
  const { data: channels = [] } = useAuthoredRows('commsResources');
  const createRow = useCreateAuthoredRow('commsResources');
  const updateRow = useUpdateAuthoredRow('commsResources');
  const deleteRow = useDeleteAuthoredRow('commsResources');
  const recordVersionSave = useRecordFormVersionSave(
    'ICS_217A',
    effectivePeriodNumber
  );
  const onSuccess = () => recordVersionSave.mutate();

  const first = channels[0] ?? null;

  const saveWorksheetField = (patch: Record<string, string>) => {
    if (!first) {
      createRow.mutate(patch, { onSuccess });
      return;
    }
    updateRow.mutate(
      { entityId: String(first.comms_resource_id), ...patch },
      { onSuccess }
    );
  };

  return (
    <NewCgFormCard
      title="Communications Resource Availability ICS-217A"
      formname="ICS_217A_CG"
      baseId="ICS_217A"
      exportLabel="Preview ICS-217A"
    >
      <NewCgHeaderBoxes
        labels={ICS217A_BOX_LABELS}
        testIdPrefix="ics217a"
      />

      <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
        <Ics204CardSection
          boxLabel={ICS217A_BOX_LABELS.frequencyBand}
          testId="ics217a-section-frequency-band"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the frequency band">
              {ICS217A_FREQUENCY_BAND_HINT}
            </BoxInfo>
          }
        >
          <SaveableField
            id="ics217a-frequency-band"
            label="Frequency Band"
            hideLabel
            placeholder="LF, MF, HF or VHF"
            value={String(first?.frequency_band ?? '')}
            onSave={(value) => saveWorksheetField({ frequency_band: value })}
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS217A_BOX_LABELS.description}
          testId="ics217a-section-description"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the description">
              {ICS217A_DESCRIPTION_HINT}
            </BoxInfo>
          }
        >
          <SaveableField
            id="ics217a-description"
            label="Description"
            hideLabel
            multiline
            placeholder="Describe the communication resources being used..."
            value={String(first?.description ?? '')}
            onSave={(value) => saveWorksheetField({ description: value })}
          />
        </Ics204CardSection>
      </div>

      <Ics204CardSection
        boxLabel={ICS217A_BOX_LABELS.channels}
        testId="ics217a-section-channels"
        className="tw:mt-0"
      >
        <BoxRowTable
          columns={ICS217A_COLUMNS}
          rows={channels}
          identityColumn="comms_resource_id"
          testId="ics217a-channels-table"
          addLabel="Add Channel"
          onSave={(entityId, patch) =>
            updateRow.mutate({ entityId, ...patch }, { onSuccess })
          }
          onDelete={(entityId) => deleteRow.mutate(entityId, { onSuccess })}
          onAdd={async () => {
            const created = await createRow.mutateAsync({});
            onSuccess();
            return String(created?.comms_resource_id ?? '');
          }}
        />
      </Ics204CardSection>

      <NewCgPreparedByBox
        baseId="ICS_217A"
        boxLabel={ICS217A_BOX_LABELS.preparedBy}
        testIdPrefix="ics217a"
      />
    </NewCgFormCard>
  );
};

export default Ics217ACard;
